import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';

import { Parent, ParentService, Sitter, SitterService } from 'us-web-services';
import { fullName } from '../utils/string';

// Angular source: apps/angular/src/js/ng/common/components/name-filter/
// Shared typeahead for filtering bookings/messages by a contact's name.
// "sitter" → queries /v2/sitters?parentId=viewerId&contacted=true.
// "parent" → queries /v2/parents?sitterId=viewerId&contacted=true.

export interface UserNameFilterOption {
  id?: number;
  name?: string;
  pictureUrl?: string;
}

interface Props {
  kind: 'sitter' | 'parent';
  viewerId?: number;
  label?: string;
  placeholder?: string;
  initialValue?: string;
  onSelect: (user: UserNameFilterOption | null) => void;
}

export const UserNameFilter = ({
  kind,
  viewerId,
  label,
  placeholder = 'Search by name...',
  initialValue = '',
  onSelect,
}: Props): JSX.Element => {
  const [query, setQuery] = useState(initialValue);
  const [options, setOptions] = useState<UserNameFilterOption[]>([]);
  const [open, setOpen] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchOptions = async (q: string): Promise<void> => {
    if (!viewerId) return;
    try {
      if (kind === 'sitter') {
        const resp = await SitterService.getByFilter({
          params: {
            parentId: viewerId,
            contacted: true,
            name: q || undefined,
            limit: 10,
            sort: 'profile.firstName',
            userId: viewerId,
          },
        });
        const data = (resp.data?.data ?? []) as Sitter[];
        setOptions(
          data.map((s) => ({
            id: s.id,
            name: fullName(s),
            pictureUrl: s.pictureUrl,
          })),
        );
      } else {
        const resp = await ParentService.getByFilter({
          params: {
            sitterId: viewerId,
            contacted: true,
            name: q || undefined,
            limit: 10,
            sort: 'profile.firstName',
            fields: 'hasValidCreditCard',
            userId: viewerId,
          },
        });
        const data = (resp.data?.data ?? []) as Parent[];
        setOptions(
          data.map((p) => ({
            id: p.id,
            name: fullName(p),
            pictureUrl: p.pictureUrl,
          })),
        );
      }
    } catch {
      setOptions([]);
    }
  };

  useEffect(() => {
    setQuery(initialValue);
  }, [initialValue]);

  const onChange = (v: string): void => {
    setQuery(v);
    setOpen(true);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      fetchOptions(v);
    }, 500);
  };

  const pick = (u: UserNameFilterOption): void => {
    setQuery(u.name ?? '');
    setOpen(false);
    onSelect(u);
  };

  const clear = (): void => {
    setQuery('');
    setOptions([]);
    setOpen(false);
    onSelect(null);
  };

  return (
    <div className='relative'>
      {label && <h3 className='text-base font-semibold mb-1'>{label}</h3>}
      <div className='flex'>
        <input
          id='name-selector'
          type='text'
          className='form-control flex-1 border border-neutral-300 rounded-l px-3 py-2'
          placeholder={placeholder}
          value={query}
          onFocus={(): void => {
            setOpen(true);
            if (!options.length) fetchOptions(query);
          }}
          onBlur={(): void => {
            setTimeout(() => setOpen(false), 150);
          }}
          onChange={(e): void => onChange(e.target.value)}
        />
        <button
          type='button'
          aria-label='Clear filter'
          className='rounded-r border border-l-0 border-neutral-300 bg-neutral-100 hover:bg-neutral-200 text-neutral-600 px-3'
          onClick={clear}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
      {open && options.length > 0 && (
        <ul className='absolute z-10 left-0 right-0 bg-white border border-neutral-200 rounded shadow mt-1 max-h-64 overflow-auto'>
          {options.map((opt) => (
            <li key={opt.id}>
              <button
                type='button'
                className='w-full text-left px-3 py-2 hover:bg-neutral-100'
                onMouseDown={(e): void => e.preventDefault()}
                onClick={(): void => pick(opt)}
              >
                {opt.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
